import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  buyersApi,
  type BuyerInterviewSummaryDto,
  type BuyerLeadQualificationDto,
  type BuyerLeadStatus,
  type QualificationVerdict,
} from "@/api/buyers";
import { extractErrorMessage } from "@/api/client";
import {
  Button,
  Skeleton,
  StatusBadge,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { buyerKeys } from "@/hooks/buyers/buyerKeys";
import type { BuyerPrequalCallSubmissionDto } from "@/api/prequalCall";
import { outcomeLabel } from "@/pages/dealFlow/PrequalificationListPage";
import { DetailSection } from "../detailBits";
import { InlineQueryError } from "../queryStates";
import { buyerLabel } from "../buyerFilters";
import { verdictTone } from "../buyerListBits";
import { BuyerInterviewDrawer } from "./BuyerInterviewDrawer";
import { InterviewOutcomePanel } from "./InterviewOutcome";
import { InterviewVersionDrawer } from "./InterviewVersionDrawer";
import { OverrideVerdictModal } from "./OverrideVerdictModal";
import { REVIEW_STATUS_LABEL } from "./CallPrequalFields";
import { PrequalCallReview } from "./PrequalCallReview";
import { useBuyerInterview, useBuyerInterviewVersions, useStartInterview } from "./useBuyerInterview";
import { usePrequalCallState } from "./usePrequalCall";

/**
 * The Qualification surface, built on the interview.
 *
 * <p>Three states, and they are genuinely different facts:</p>
 * <ul>
 *   <li><b>Never interviewed</b> — plus, possibly, a HISTORICAL assessment from
 *       the retired five-dropdown editor, which still renders read-only.</li>
 *   <li><b>In progress</b> — coverage only. 🔴 No verdict, no score, nothing
 *       that hints at one: the server withholds the outcome until completion
 *       precisely so no surface can steer what is being written down.</li>
 *   <li><b>Completed</b> — six dimensions, each with the rule that produced it,
 *       the knockouts, the coherence flags and the roll-up.</li>
 * </ul>
 */
export function QualificationInterviewPanel({
  buyerLeadId,
  buyerName,
  qualification,
  status,
  canEdit,
}: {
  buyerLeadId: string;
  /** Printed at the top of an interview's print view. */
  buyerName?: string;
  /** The standing assessment. After a completed interview this is a PROJECTION
   *  of it; before any interview it is a historical row from the retired
   *  editor. Read-only either way. */
  qualification?: BuyerLeadQualificationDto | null;
  status: BuyerLeadStatus;
  canEdit: boolean;
}) {
  const [conducting, setConducting] = useState(false);
  const [overriding, setOverriding] = useState(false);
  const [viewingVersion, setViewingVersion] = useState<number | null>(null);
  const [reviewing, setReviewing] = useState<string | null>(null);

  const toast = useToast();
  const interviewQuery = useBuyerInterview(buyerLeadId);
  const versionsQuery = useBuyerInterviewVersions(buyerLeadId);
  const start = useStartInterview(buyerLeadId);
  // The call state is a Buyers.Update read, so a reader who cannot edit never
  // asks for it. A failed read hides the call's line rather than the card.
  const callQuery = usePrequalCallState(buyerLeadId, canEdit);
  const latestCallPrequal = callQuery.data?.latestCallPrequal ?? null;

  const interview = interviewQuery.data ?? null;
  // Narrowed once, so every consumer below reads a non-null interview rather
  // than re-asserting it. `outcome` is absent on anything but a completed one —
  // that is the server's contract, not a client-side hide.
  const completed = interview?.status === "Completed" ? interview : null;
  const outcome = completed?.outcome ?? null;
  const inProgressRow = interview?.status === "InProgress" ? interview : null;
  const versions = versionsQuery.data ?? [];

  const beginInterview = () =>
    start.mutate(undefined, {
      onSuccess: () => setConducting(true),
      onError: (error: unknown) => toast.error(extractErrorMessage(error)),
    });

  const actionLabel = inProgressRow
    ? "Resume interview"
    : outcome
      ? "Re-interview"
      : "Start interview";

  const action = canEdit ? (
    <Button
      variant="text"
      size="sm"
      onClick={beginInterview}
      loading={start.isPending}
      disabled={start.isPending}
      data-testid="interview-start"
    >
      {actionLabel}
    </Button>
  ) : null;

  const drawers = (
    <>
      {canEdit && (
        <BuyerInterviewDrawer
          buyerLeadId={buyerLeadId}
          open={conducting}
          onClose={() => setConducting(false)}
        />
      )}
      <InterviewVersionDrawer
        buyerLeadId={buyerLeadId}
        buyerName={buyerName}
        version={viewingVersion}
        onClose={() => setViewingVersion(null)}
        completedVersions={versions
          .filter((v) => v.status === "Completed")
          .map((v) => v.version)}
      />
      {reviewing && (
        <PrequalCallReview
          buyerId={buyerLeadId}
          submissionId={reviewing}
          open
          onClose={() => setReviewing(null)}
        />
      )}
      {outcome && canEdit && (
        <OverrideVerdictModal
          buyerLeadId={buyerLeadId}
          open={overriding}
          computedVerdict={outcome.computedVerdict}
          currentVerdict={outcome.effectiveVerdict}
          currentReason={outcome.overrideReason}
          onClose={() => setOverriding(false)}
        />
      )}
    </>
  );

  if (interviewQuery.isLoading) {
    return (
      <DetailSection title="Assessment">
        <Skeleton className="h-24 w-full" />
      </DetailSection>
    );
  }

  if (interviewQuery.isError) {
    return (
      <DetailSection title="Assessment" action={action}>
        <InlineQueryError
          what="the qualification interview"
          onRetry={() => void interviewQuery.refetch()}
          retrying={interviewQuery.isFetching}
        />
        {drawers}
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Assessment" action={action}>
      <div className="grid gap-space-4">
        {!interview && !qualification && (
          <p className="text-small text-gray-mid m-0">
            Not interviewed yet. An uninterviewed buyer reads as Unknown, not as
            a pass.
          </p>
        )}

        {inProgressRow && (
          <InProgressNotice
            recorded={inProgressRow.progress.reduce((n, p) => n + p.spineAnswered, 0)}
            required={inProgressRow.progress.reduce((n, p) => n + p.spineTotal, 0)}
          />
        )}

        {completed && outcome && (
          <>
            <InterviewOutcomePanel
              outcome={outcome}
              headline={
                <div className="flex flex-wrap items-center gap-space-2">
                  <p className="text-caption text-gray-mid m-0">
                    Interview v{completed.version}
                    {completed.conductedByUserName
                      ? ` · ${completed.conductedByUserName}`
                      : ""}
                    {completed.completedAtUtc ? " · " : ""}
                    {completed.completedAtUtc && (
                      <Timestamp value={completed.completedAtUtc} />
                    )}
                  </p>
                  {/* Read-only for everyone who can see this card — viewing is
                      not gated on canEdit, unlike the actions beside it. */}
                  <Button
                    variant="text"
                    size="sm"
                    onClick={() => setViewingVersion(completed.version)}
                    data-testid="interview-view"
                  >
                    View interview
                  </Button>
                </div>
              }
            />
            <div className="flex flex-wrap items-center gap-space-2">
              {canEdit && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOverriding(true)}
                  data-testid="interview-override"
                >
                  Override assessment
                </Button>
              )}
              {/* D8: the prequalification is optional on the call and can be
                  finished later, from the same screen, on the completed
                  interview. Offered until a call prequal exists; after that
                  its line below carries the next step. */}
              {canEdit && callQuery.isSuccess && !latestCallPrequal && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConducting(true)}
                  data-testid="interview-finish-prequal"
                >
                  Finish prequalification
                </Button>
              )}
              <AdvanceToQualifiedAction
                buyerLeadId={buyerLeadId}
                verdict={outcome.effectiveVerdict}
                status={status}
                canEdit={canEdit}
              />
            </div>
          </>
        )}

        {latestCallPrequal && (
          <CallPrequalLine
            prequal={latestCallPrequal}
            onReview={canEdit ? () => setReviewing(latestCallPrequal.submissionId) : undefined}
          />
        )}

        {/* 🔴 The HISTORICAL assessment. Buyers assessed before the interview
            existed have a `BuyerLeadQualification` row and no interview — it
            still renders, read-only, financing dimension and all. It is hidden
            once a completed interview exists, because from that point the row
            is merely a projection of the interview above and showing both would
            be the same verdict twice. */}
        {qualification && !outcome && (
          <HistoricalAssessment qualification={qualification} />
        )}

        {/* An empty history and a history that failed to load render the same
            way if nothing says otherwise — and "no earlier interviews" is a
            claim about the record, where a failed read is a fact about the
            request. */}
        {versionsQuery.isError ? (
          <InlineQueryError
            what="the interview history"
            onRetry={() => void versionsQuery.refetch()}
            retrying={versionsQuery.isFetching}
          />
        ) : (
          <VersionHistory
            versions={versions}
            loading={versionsQuery.isLoading}
            onOpen={setViewingVersion}
          />
        )}
      </div>
      {drawers}
    </DetailSection>
  );
}

/**
 * The newest prequalification taken on a call: its outcome, reference and where
 * its review stands, with the way into the review while nothing has been sent
 * or decided. The outcome shows here, on the record, and never in the live
 * interview drawer.
 */
function CallPrequalLine({
  prequal,
  onReview,
}: {
  prequal: BuyerPrequalCallSubmissionDto;
  onReview?: () => void;
}) {
  return (
    <div
      className="flex flex-wrap items-center gap-space-2 text-small"
      data-testid="interview-call-prequal"
    >
      <span className="text-caption uppercase tracking-wide text-gray-mid">
        Prequalified on a call
      </span>
      <StatusBadge tone={prequal.outcome === "Pass" ? "ok" : "warn"}>
        {outcomeLabel(prequal.outcome)}
      </StatusBadge>
      {prequal.referenceNumber && <span className="text-onyx">{prequal.referenceNumber}</span>}
      <span className="text-gray-mid" data-testid="interview-call-prequal-status">
        {REVIEW_STATUS_LABEL[prequal.reviewStatus]}
      </span>
      {onReview && prequal.reviewStatus === "awaitingReview" && (
        <Button
          size="sm"
          variant="secondary"
          onClick={onReview}
          data-testid="interview-call-prequal-review"
        >
          Review &amp; send
        </Button>
      )}
    </div>
  );
}

/**
 * 🔴 COVERAGE ONLY. This is the one number an in-progress interview may show,
 * and it says how much was asked — never how it went.
 */
function InProgressNotice({ recorded, required }: { recorded: number; required: number }) {
  return (
    <p className="text-small text-gray-mid m-0" data-testid="interview-in-progress">
      Interview in progress — {recorded} of {required} required answers recorded.
      The assessment is computed when the interview is completed.
    </p>
  );
}

/**
 * Advance the STATUS ladder to Qualified — one click, by a person.
 *
 * <p>🔴 <b>Never automatic, and never part of completing the interview.</b>
 * Reaching Pass is evidence; moving a buyer along the pipeline is a decision.
 * It is a button, fired by a click, and it is HIDDEN rather than disabled when
 * it does not apply — the repo's dominant convention.</p>
 *
 * <p>The <c>qualifiedAtUtc</c> MILESTONE is a different thing and is already
 * stamped server-side, forward-only, the moment the roll-up reaches Pass
 * (<c>BuyerQualificationInterviewService.ProjectOntoQualificationAsync</c>).
 * This action moves the ladder only; the two are deliberately separate, so a
 * buyer can be evidenced as qualified without being marched down the
 * pipeline.</p>
 */
export function AdvanceToQualifiedAction({
  buyerLeadId,
  verdict,
  status,
  canEdit,
}: {
  buyerLeadId: string;
  verdict: QualificationVerdict;
  status: BuyerLeadStatus;
  canEdit: boolean;
}) {
  const toast = useToast();
  const qc = useQueryClient();

  const advance = useMutation({
    mutationFn: () => buyersApi.setStatus(buyerLeadId, { status: "Qualified" }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: buyerKeys.all });
      toast.success("Buyer advanced to Qualified.");
    },
    onError: (error: unknown) => toast.error(extractErrorMessage(error)),
  });

  if (!canEdit || verdict !== "Pass" || status === "Qualified") return null;

  return (
    <Button
      size="sm"
      onClick={() => advance.mutate()}
      loading={advance.isPending}
      disabled={advance.isPending}
      data-testid="advance-to-qualified"
    >
      Advance to Qualified
    </Button>
  );
}

/**
 * The retired editor's assessment, read-only.
 *
 * <p>It is not editable, and there is no path back to editing it: changing an
 * assessment means conducting an interview. `financing` renders because the row
 * carries one — the dimension is gone from the model, and dropping it from the
 * display would silently discard a recorded judgement.</p>
 */
function HistoricalAssessment({
  qualification: q,
}: {
  qualification: BuyerLeadQualificationDto;
}) {
  const dimensions: { label: string; verdict: QualificationVerdict; note?: string | null }[] = [
    { label: "Financial capacity", verdict: q.financialCapacity, note: q.financialCapacityNote },
    { label: "Geography", verdict: q.geography, note: q.geographyNote },
    { label: "Experience", verdict: q.experience, note: q.experienceNote },
    { label: "Timeline", verdict: q.timeline, note: q.timelineNote },
    { label: "Financing", verdict: q.financing, note: q.financingNote },
  ];

  return (
    <div
      className="rounded-md border border-border-light p-space-3"
      data-testid="historical-assessment"
    >
      <div className="flex flex-wrap items-center gap-space-3">
        <h4 className="font-display font-bold text-small text-onyx m-0">
          Earlier assessment
        </h4>
        <StatusBadge tone={verdictTone(q.overallVerdict)}>
          {buyerLabel(q.overallVerdict)}
        </StatusBadge>
        {q.isOverridden && (
          <span className="text-caption text-gray-mid">
            Overridden{q.overrideReason ? ` — ${q.overrideReason}` : ""}
          </span>
        )}
      </div>
      <p className="text-caption text-gray-mid m-0 mt-space-1">
        Recorded before the qualification interview existed. Read-only —
        interviewing this buyer replaces it.
      </p>

      <div className="grid gap-space-2 mt-space-3">
        {dimensions.map((d) => (
          <div key={d.label} className="flex items-start gap-space-3">
            <div className="w-44 text-caption uppercase tracking-wide text-gray-mid">
              {d.label}
            </div>
            <div className="flex-1 min-w-0">
              <StatusBadge tone={verdictTone(d.verdict)}>{buyerLabel(d.verdict)}</StatusBadge>
              {d.note && <div className="text-caption text-gray-mid mt-space-1">{d.note}</div>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Every interview conducted, newest first. Each COMPLETED one opens read-only. */
function VersionHistory({
  versions,
  loading,
  onOpen,
}: {
  versions: BuyerInterviewSummaryDto[];
  loading: boolean;
  onOpen: (version: number) => void;
}) {
  if (loading) return <Skeleton className="h-8 w-48" />;
  if (versions.length === 0) return null;

  return (
    <div data-testid="interview-versions">
      <h4 className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-2">
        Interview history
      </h4>
      <ul className="m-0 pl-0 list-none grid gap-space-1">
        {versions.map((v) => (
          <li key={v.version} className="flex flex-wrap items-center gap-space-2 text-small">
            <span className="text-onyx">v{v.version}</span>
            <span className="text-gray-mid">
              {v.status === "Completed" ? "Completed" : "In progress"}
            </span>
            {v.completedAtUtc && (
              <span className="text-gray-mid">
                <Timestamp value={v.completedAtUtc} />
              </span>
            )}
            {v.conductedByUserName && (
              <span className="text-gray-mid">· {v.conductedByUserName}</span>
            )}
            {/* The roll-up is shown for a COMPLETED interview only — the
                summary carries none for one still in progress, and inventing a
                placeholder there would be the rating hint this module refuses
                to render. */}
            {v.status === "Completed" && v.effectiveVerdict && (
              <StatusBadge tone={verdictTone(v.effectiveVerdict)}>
                {buyerLabel(v.effectiveVerdict)}
              </StatusBadge>
            )}
            {v.isOverridden && <span className="text-caption text-gray-mid">overridden</span>}
            {/* Completed versions only: an interview still in progress has no
                result to review, and the view would be the rating hint the
                in-progress state withholds. */}
            {v.status === "Completed" && (
              <button
                type="button"
                onClick={() => onOpen(v.version)}
                className="text-gold-shadow hover:underline"
                aria-label={`View interview v${v.version}`}
                data-testid={`interview-version-view-${v.version}`}
              >
                View
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
